/** Generic multi-company catalog + deterministic generators for Profiles Dashboard. */

export type SiteType =
  | 'stadium'
  | 'academy'
  | 'training_ground'
  | 'fan_zone'
  | 'retail_store'
  | 'office'
  | 'warehouse'
  | 'hotel'
  | 'school'
  | 'leisure'

export type SiteDef = {
  id: string
  companyId: string
  name: string
  type: SiteType
  baseKwh: number
  eventUplift: number
  seasonalityAmp: number
  weekendFactor: number
}

export type CompanyDef = {
  id: string
  name: string
  sector: string
}

export const COMPANIES: CompanyDef[] = [
  { id: '2', name: 'Newcastle United', sector: 'Sports & venues' },
  { id: '11', name: 'Meridian Retail Group', sector: 'Retail' },
  { id: '18', name: 'Northbridge Estates', sector: 'Commercial property' },
  { id: '24', name: 'Harbour City Council', sector: 'Public facilities' },
]

export const SITES: SiteDef[] = [
  // Newcastle United
  {
    id: '2',
    companyId: '2',
    name: "St James' Park",
    type: 'stadium',
    baseKwh: 15600,
    eventUplift: 0.81,
    seasonalityAmp: 0.35,
    weekendFactor: 1.05,
  },
  {
    id: '1',
    companyId: '2',
    name: 'Academy',
    type: 'academy',
    baseKwh: 695,
    eventUplift: -0.1,
    seasonalityAmp: 0.28,
    weekendFactor: 0.7,
  },
  {
    id: '115',
    companyId: '2',
    name: 'Training Ground',
    type: 'training_ground',
    baseKwh: 1650,
    eventUplift: 0.04,
    seasonalityAmp: 0.4,
    weekendFactor: 0.75,
  },
  {
    id: '166',
    companyId: '2',
    name: 'Fan Zone',
    type: 'fan_zone',
    baseKwh: 2070,
    eventUplift: 0.27,
    seasonalityAmp: 0.12,
    weekendFactor: 1.18,
  },
  {
    id: '225',
    companyId: '2',
    name: 'Metro Centre',
    type: 'retail_store',
    baseKwh: 151,
    eventUplift: -0.08,
    seasonalityAmp: 0.18,
    weekendFactor: 1.15,
  },
  {
    id: '261',
    companyId: '2',
    name: 'Media Centre',
    type: 'office',
    baseKwh: 58,
    eventUplift: -0.2,
    seasonalityAmp: 0.1,
    weekendFactor: 0.55,
  },
  // Meridian Retail
  {
    id: '501',
    companyId: '11',
    name: 'Westgate Mall',
    type: 'retail_store',
    baseKwh: 4200,
    eventUplift: 0.22,
    seasonalityAmp: 0.2,
    weekendFactor: 1.35,
  },
  {
    id: '502',
    companyId: '11',
    name: 'Riverside Outlet',
    type: 'retail_store',
    baseKwh: 1850,
    eventUplift: 0.15,
    seasonalityAmp: 0.16,
    weekendFactor: 1.4,
  },
  {
    id: '503',
    companyId: '11',
    name: 'Distribution Hub',
    type: 'warehouse',
    baseKwh: 6100,
    eventUplift: 0.05,
    seasonalityAmp: 0.12,
    weekendFactor: 0.9,
  },
  // Northbridge Estates
  {
    id: '601',
    companyId: '18',
    name: 'Quayside Tower',
    type: 'office',
    baseKwh: 9800,
    eventUplift: 0.08,
    seasonalityAmp: 0.22,
    weekendFactor: 0.35,
  },
  {
    id: '602',
    companyId: '18',
    name: 'Central Business Park',
    type: 'office',
    baseKwh: 5400,
    eventUplift: 0.06,
    seasonalityAmp: 0.2,
    weekendFactor: 0.4,
  },
  {
    id: '603',
    companyId: '18',
    name: 'Harbour Hotel',
    type: 'hotel',
    baseKwh: 7200,
    eventUplift: 0.18,
    seasonalityAmp: 0.25,
    weekendFactor: 1.12,
  },
  // Harbour City Council
  {
    id: '701',
    companyId: '24',
    name: 'Civic Centre',
    type: 'office',
    baseKwh: 3100,
    eventUplift: 0.1,
    seasonalityAmp: 0.24,
    weekendFactor: 0.3,
  },
  {
    id: '702',
    companyId: '24',
    name: 'City Leisure Centre',
    type: 'leisure',
    baseKwh: 4500,
    eventUplift: 0.2,
    seasonalityAmp: 0.15,
    weekendFactor: 1.25,
  },
  {
    id: '703',
    companyId: '24',
    name: 'North High School',
    type: 'school',
    baseKwh: 2100,
    eventUplift: 0.12,
    seasonalityAmp: 0.3,
    weekendFactor: 0.25,
  },
]

export const sitesForCompany = (companyId: string) => SITES.filter((s) => s.companyId === companyId)

export const getSite = (siteId: string) => SITES.find((s) => s.id === siteId)

function hash(str: string) {
  let h = 0
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0
  return h
}

function seeded(seed: number) {
  let s = seed || 1
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 0xffffffff
  }
}

function pad(n: number) {
  return n < 10 ? `0${n}` : `${n}`
}

export function toIso(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export function eachDay(start: string, end: string) {
  const out: string[] = []
  const cur = new Date(start + 'T12:00:00')
  const last = new Date(end + 'T12:00:00')
  while (cur <= last) {
    out.push(toIso(cur))
    cur.setDate(cur.getDate() + 1)
  }
  return out
}

export function daysBetween(start: string, end: string) {
  const a = new Date(start + 'T12:00:00').getTime()
  const b = new Date(end + 'T12:00:00').getTime()
  return Math.max(1, Math.round((b - a) / 86400000) + 1)
}

/** Event days: ~every 7th day for event-heavy sites, rarer otherwise */
function isEventDay(site: SiteDef, date: string, rnd: () => number) {
  const d = new Date(date + 'T12:00:00')
  const dow = d.getDay()
  if (site.type === 'stadium' || site.type === 'leisure' || site.type === 'fan_zone') {
    return dow === 6 || (dow === 0 && rnd() > 0.4) || (dow === 3 && rnd() > 0.85)
  }
  if (site.type === 'retail_store' || site.type === 'hotel') {
    return dow === 5 || dow === 6
  }
  if (site.type === 'school') return false
  return rnd() > 0.92
}

export type DailyPoint = {
  date: string
  actual: number
  predicted: number
  isEvent: boolean
  isWeekend: boolean
  tempC: number
}

export function buildDailySeries(site: SiteDef, start: string, end: string): DailyPoint[] {
  const rnd = seeded(hash(`${site.id}:${start}:${end}`))
  const days = eachDay(start, end)
  return days.map((date, i) => {
    const d = new Date(date + 'T12:00:00')
    const dow = d.getDay()
    const isWeekend = dow === 0 || dow === 6
    const month = d.getMonth()
    const season = 1 + site.seasonalityAmp * Math.sin(((month - 1) / 12) * Math.PI * 2)
    const weekend = isWeekend ? site.weekendFactor : 1
    const event = isEventDay(site, date, rnd)
    const eventMult = event ? 1 + site.eventUplift : 1
    const noise = 0.92 + rnd() * 0.16
    const actual = Math.round(site.baseKwh * season * weekend * eventMult * noise)
    const predNoise = 0.94 + rnd() * 0.1
    const predicted = Math.round(actual * predNoise * (0.97 + (i % 5) * 0.005))
    const tempC =
      Math.round((8 + 10 * Math.sin(((month - 2) / 12) * Math.PI * 2) + rnd() * 4) * 10) / 10
    return { date, actual, predicted, isEvent: event, isWeekend, tempC }
  })
}

export function kpisFromSeries(series: DailyPoint[]) {
  const total = series.reduce((s, r) => s + r.actual, 0)
  const predicted = series.reduce((s, r) => s + r.predicted, 0)
  const eventDays = series.filter((r) => r.isEvent)
  const nonEvent = series.filter((r) => !r.isEvent)
  const avg = series.length ? Math.round(total / series.length) : 0
  const eventAvg = eventDays.length
    ? Math.round(eventDays.reduce((s, r) => s + r.actual, 0) / eventDays.length)
    : 0
  const nonEventAvg = nonEvent.length
    ? Math.round(nonEvent.reduce((s, r) => s + r.actual, 0) / nonEvent.length)
    : 0
  const upliftPct =
    nonEventAvg > 0 ? Math.round(((eventAvg - nonEventAvg) / nonEventAvg) * 1000) / 10 : 0
  let apeSum = 0
  series.forEach((r) => {
    apeSum += Math.abs(r.predicted - r.actual) / Math.max(1, r.actual)
  })
  const wape = series.length ? Math.round((apeSum / series.length) * 1000) / 10 : 0
  const peak = series.reduce((m, r) => Math.max(m, r.actual), 0)
  return {
    total,
    predicted,
    avg,
    eventAvg,
    nonEventAvg,
    upliftPct,
    wape,
    peak,
    eventCount: eventDays.length,
    days: series.length,
  }
}

export function monthlySeasonality(series: DailyPoint[]) {
  const months = Array.from({ length: 12 }, (_, i) => i + 1)
  const buckets = months.map((m) => {
    const rows = series.filter((r) => new Date(r.date + 'T12:00:00').getMonth() + 1 === m)
    const mean = rows.length ? rows.reduce((s, r) => s + r.actual, 0) / rows.length : 0
    return mean
  })
  const overall = buckets.filter((v) => v > 0)
  const base = overall.length ? overall.reduce((a, b) => a + b, 0) / overall.length : 1
  return {
    categories: [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ],
    values: buckets.map((v) => (v > 0 ? Math.round((v / base) * 100) / 100 : 0)),
  }
}

export function weekdayHourHeatmap(site: SiteDef) {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
  const hours = [0, 3, 6, 9, 12, 15, 18, 21]
  const rnd = seeded(hash(`heat:${site.id}`))
  return days.map((day, di) => ({
    name: day,
    data: hours.map((h) => {
      const weekend = di >= 5
      let load = 0.45
      if (site.type === 'office' || site.type === 'school') {
        load = weekend ? 0.25 : h >= 9 && h <= 18 ? 1.15 : 0.4
      } else if (site.type === 'retail_store') {
        load = h >= 9 && h <= 21 ? (weekend ? 1.3 : 1.0) : 0.35
      } else if (site.type === 'stadium' || site.type === 'leisure') {
        load = h >= 15 && h <= 21 ? 1.25 : 0.55
        if (weekend && h >= 12) load *= 1.15
      } else if (site.type === 'warehouse') {
        load = h >= 6 && h <= 18 ? 1.1 : 0.5
      } else if (site.type === 'hotel') {
        load = h >= 6 && h <= 10 ? 1.2 : h >= 18 ? 1.1 : 0.7
      } else {
        load = weekend ? site.weekendFactor * 0.7 : h >= 8 && h <= 18 ? 1 : 0.5
      }
      return { x: `${h}:00`, y: Math.round((load + rnd() * 0.08) * 100) }
    }),
  }))
}

export function weatherScatter(series: DailyPoint[]) {
  return series
    .filter((r) => !r.isEvent)
    .slice(0, 80)
    .map((r) => [r.tempC, r.actual] as [number, number])
}

export function eventImpactBars(series: DailyPoint[]) {
  const event = series.filter((r) => r.isEvent).slice(-8)
  const baseline =
    series.filter((r) => !r.isEvent).reduce((s, r) => s + r.actual, 0) /
    Math.max(1, series.filter((r) => !r.isEvent).length)
  return event.map((r) => ({
    label: r.date.slice(5),
    actual: r.actual,
    ratio: Math.round((r.actual / Math.max(1, baseline)) * 100) / 100,
  }))
}

export function operationalSplit(series: DailyPoint[]) {
  const groups = [
    { label: 'Weekday', test: (r: DailyPoint) => !r.isWeekend && !r.isEvent },
    { label: 'Weekend', test: (r: DailyPoint) => r.isWeekend && !r.isEvent },
    { label: 'Event day', test: (r: DailyPoint) => r.isEvent },
  ]
  return groups.map((g) => {
    const rows = series.filter(g.test)
    const mean = rows.length ? Math.round(rows.reduce((s, r) => s + r.actual, 0) / rows.length) : 0
    return { label: g.label, mean, count: rows.length }
  })
}

export function yearlyTrend(site: SiteDef) {
  const years = [2021, 2022, 2023, 2024, 2025, 2026]
  const rnd = seeded(hash(`year:${site.id}`))
  return years.map((year, i) => ({
    year: String(year),
    mean: Math.round(site.baseKwh * (0.85 + i * 0.04 + rnd() * 0.08)),
  }))
}

export function profileByHour(site: SiteDef) {
  const hours = Array.from({ length: 24 }, (_, h) => `${h}:00`)
  const rnd = seeded(hash(`prof:${site.id}`))
  const build = (peakHour: number, amplitude: number) =>
    hours.map((_, h) => {
      const dist = Math.min(Math.abs(h - peakHour), 24 - Math.abs(h - peakHour))
      const v = site.baseKwh / 48 + amplitude * Math.exp((-dist * dist) / 18) * (site.baseKwh / 24)
      return Math.round(v * (0.9 + rnd() * 0.2))
    })
  return {
    categories: hours,
    series: [
      { name: 'Typical weekday', data: build(11, 1.1) },
      { name: 'Typical weekend', data: build(14, site.weekendFactor) },
      { name: 'Event / peak day', data: build(19, 1 + Math.max(0.2, site.eventUplift)) },
    ],
  }
}

export type DashboardSectionId =
  | 'kpis'
  | 'timeseries'
  | 'seasonality'
  | 'heatmap'
  | 'events'
  | 'weather'
  | 'operational'
  | 'profiles'
  | 'yearly'

export type ChartKind = 'line' | 'area' | 'bar'

export type SectionConfig = {
  id: DashboardSectionId
  label: string
  visible: boolean
  chartType: ChartKind
}

export const DEFAULT_SECTIONS: SectionConfig[] = [
  { id: 'kpis', label: 'KPI summary', visible: true, chartType: 'bar' },
  { id: 'timeseries', label: 'Consumption vs forecast', visible: true, chartType: 'line' },
  { id: 'seasonality', label: 'Monthly seasonality', visible: true, chartType: 'line' },
  { id: 'heatmap', label: 'Weekday × hour heatmap', visible: true, chartType: 'bar' },
  { id: 'events', label: 'Event impact', visible: true, chartType: 'bar' },
  { id: 'weather', label: 'Weather sensitivity', visible: true, chartType: 'line' },
  { id: 'operational', label: 'Operational split', visible: true, chartType: 'bar' },
  { id: 'profiles', label: 'Half-hour profiles', visible: true, chartType: 'line' },
  { id: 'yearly', label: 'Yearly trend', visible: true, chartType: 'area' },
]

export const STORAGE_KEY = 'profiles.dashboard.sections.v1'
